import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import builderLogo from '../../assets/Builder-Logo.png';
import quoteService, { Quote } from '../../services/quoteService';
import { format, parseISO } from 'date-fns';
import customerService from '../../services/customerService';
import { API_URL, AUTH_TOKEN_KEY, ENDPOINTS } from '../../config';
// @ts-ignore
import html2pdf from 'html2pdf.js';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 95vw;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  padding: 8px 16px;
  border-bottom: none;
  text-align: right;
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  color: #333;
  margin: 0;
  display: none;
`;

const ModalBody = styled.div`
  padding: 16px;
`;

const PrintableContent = styled.div`
  background: white;
  font-family: Arial, sans-serif;
  padding: 20px;
  max-width: 1100px;
  margin: 0 auto;
  
  @media print {
    margin: 0;
    padding: 20px;
    font-size: 12px;
    page-break-inside: avoid;
    transform: rotate(0deg);
  }
`;

const QuoteContainer = styled.div`
  border: 2px solid #333;
  padding: 20px;
  margin-bottom: 20px;
  background: white;
`;

const QuoteTitle = styled.h1`
  text-align: center;
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin: 0 0 20px 0;
  padding: 0;
`;

const QuoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const CompanyLogo = styled.div`
  width: 180px;
  display: flex;
  flex-direction: column;
  
  img {
    max-width: 100%;
    height: auto;
    margin-bottom: 5px;
  }
  
  .tagline {
    font-style: italic;
    font-size: 14px;
    color: #042B41;
    margin-top: 0;
    font-family: cursive;
  }
`;

const CompanyInfo = styled.div`
  text-align: right;
  font-size: 11px;
  line-height: 1.3;
  color: #333;
  
  p {
    margin: 1px 0;
  }
  
  .company-name {
    font-weight: bold;
    font-size: 12px;
  }
`;

const MainContentContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
`;

const LeftSection = styled.div`
  flex: 1;
`;

const RightSection = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
`;

const InfoTable = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  margin-bottom: 15px;
  border: 2px solid #333;
`;

const InfoCell = styled.div`
  padding: 12px;
  border: 1px solid #333;
  font-size: 13px;
  min-height: 50px;
`;

const InfoLabel = styled.div`
  font-weight: bold;
  font-size: 12px;
  margin-bottom: 6px;
  color: #333;
`;

const QuoteLabel = styled.div`
  background-color: #0096db;
  color: white;
  padding: 8px 15px;
  text-align: center;
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 0;
  border: 2px solid #333;
`;

const QuoteDetails = styled.div`
  display: flex;
  flex-direction: column;
  border: 2px solid #333;
  border-top: none;
  background-color: white;
`;

const QuoteDetail = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 6px 15px;
  font-size: 13px;
  border-bottom: 1px solid #333;
  
  &:last-child {
    border-bottom: none;
  }
  
  span:first-child {
    font-weight: 600;
  }
  
  span:last-child {
    text-align: right;
    font-weight: 500;
  }
`;

const QuoteTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 15px 0;
  font-size: 12px;
  border: 2px solid #333;
  
  th, td {
    border: 1px solid #333;
    padding: 6px 4px;
    text-align: center;
  }
  
  th {
    background-color: #042B41;
    color: white;
    font-size: 11px;
    font-weight: 600;
    text-align: center;
    vertical-align: middle;
    height: 35px;
  }
  
  td {
    padding: 6px 4px;
    vertical-align: middle;
    font-size: 11px;
    height: 30px;
  }
  
  .code-column { 
    width: 12%; 
    text-align: left;
  }
  .description-column { 
    width: 18%; 
    text-align: left;
  }
  .quantity-column { width: 8%; }
  .unit-price-column { 
    width: 18%; 
    text-align: right;
  }
  .total-price-column { 
    width: 18%; 
    text-align: right;
  }
`;

const OrderTotal = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-left: auto;
  width: 220px;
  background-color: #f8f9fa;
  border: 2px solid #333;
  margin-top: 15px;
  
  div {
    padding: 6px 12px;
    border: 1px solid #333;
    font-size: 13px;
  }
  
  div:nth-child(even) {
    text-align: right;
    font-weight: 700;
    background-color: white;
  }
  
  div:nth-child(odd) {
    text-align: left;
    font-weight: 600;
    background-color: #e9ecef;
  }
`;

const QuoteInfo = styled.div`
  margin: 20px 0 0 0;
  padding: 12px;
  border: 2px solid #333;
  font-size: 11px;
  line-height: 1.4;
  background-color: #f9f9f9;
  
  p {
    margin: 4px 0;
  }
  
  p:first-child {
    margin-top: 0;
  }
  
  p:last-child {
    margin-bottom: 0;
  }
  
  strong {
    font-weight: 700;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
`;

const Button = styled.button<{ primary?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  background-color: ${props => props.disabled ? '#ccc' : (props.primary ? '#042B41' : 'white')};
  color: ${props => props.disabled ? '#666' : (props.primary ? 'white' : '#042B41')};
  border: ${props => props.primary ? 'none' : '1px solid #042B41'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover {
    background-color: ${props => props.disabled ? '#ccc' : (props.primary ? '#0A3D5A' : '#f9fafb')};
  }
`;

interface QuoteItem {
  id?: string;
  quote_id?: string;
  code: string;
  style_code?: string | null;
  description: string;
  sku?: string;
  units?: number;
  boxes?: number;
  pieces?: number;
  m2?: number;
  blm?: number;
  unit_price: number;
  total_price: number;
  created_at?: string;
  updated_at?: string;
  quantity?: {
    units: number | null;
    boxes: number | null;
    pieces: number | null;
    m2: number | null;
    blm: number | null;
    total: number;
  };
  unitPrice?: number;
  totalPrice?: number;
}

interface QuoteData {
  quoteNumber: string;
  date: string;
  reference: string;
  customerName: string;
  items: QuoteItem[];
  orderTotal: number;
  gst: number;
  grandTotal: number;
  notes: string;
}

interface QuoteViewProps {
  quoteId: string;
  onClose: () => void;
  printMode?: boolean;
}

// Add quote PDF generation function
const generateQuotePdf = async (quoteId: string): Promise<Blob> => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);

  if (!token) {
    throw new Error("Authentication required to generate quote PDF");
  }

  console.log("Attempting to generate PDF for quote:", quoteId);
  console.log(
    "Using endpoint:",
    `${API_URL}${ENDPOINTS.QUOTES}/${quoteId}/pdf`
  );

  try {
    const response = await fetch(
      `${API_URL}${ENDPOINTS.QUOTES}/${quoteId}/pdf`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/pdf",
        },
      }
    );

    console.log("PDF generation response status:", response.status);
    console.log("PDF generation response headers:", response.headers);

    if (!response.ok) {
      // Log detailed error information
      const errorText = await response.text();
      console.error("PDF generation failed with status:", response.status);
      console.error("Error response body:", errorText);

      if (response.status === 404) {
        throw new Error(
          "PDF generation endpoint not found. Please contact support."
        );
      } else if (response.status === 403) {
        throw new Error("You do not have permission to download this quote.");
      } else if (response.status === 401) {
        throw new Error("Authentication required to generate quote PDF");
      } else {
        throw new Error(
          `Failed to generate quote PDF: ${response.status} ${response.statusText}`
        );
      }
    }

    const blob = await response.blob();
    console.log("PDF blob generated successfully, size:", blob.size, "bytes");
    return blob;
  } catch (error) {
    console.error("Error in generateQuotePdf:", error);
    throw error;
  }
};

const QuoteView: React.FC<QuoteViewProps> = ({ quoteId, onClose, printMode }) => {
  const [quote, setQuote] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const [downloading, setDownloading] = useState(false);
  const pdfRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Fetch quote data when component mounts or quoteId changes
  useEffect(() => {
    const fetchQuoteData = async () => {
      // Reset all state when quoteId changes
      setLoading(true);
      setError(null);
      setQuote(null);
      setCompanyName('');
      setDownloading(false);

      try {
        console.log('Fetching quote with ID:', quoteId);
        const quoteData = await quoteService.getQuoteById(quoteId);
        console.log('Quote data received:', quoteData);

        setQuote(quoteData);

        // If we have a customer_id but no customer name, fetch the customer details
        if (quoteData.customer_id && (!quoteData.customer_name)) {
          try {
            console.log('Fetching customer details for customer_id:', quoteData.customer_id);
            const customerData = await customerService.getCustomerById(quoteData.customer_id);
            if (customerData && customerData.name) {
              console.log('Found customer name:', customerData.name);
              setCompanyName(customerData.name);
            }
          } catch (customerErr) {
            console.error('Error fetching customer details:', customerErr);
            // Continue with the quote even if customer details fetch fails
          }
        }
      } catch (err) {
        console.error('Error fetching quote:', err);
        let errorMessage = 'Failed to load quote. Please try again.';

        // Provide more specific error messages based on the error type
        if (err && typeof err === 'object' && 'response' in err) {
          const apiError = err as any;
          if (apiError.response?.status === 404) {
            errorMessage = 'Quote not found. It may have been deleted or the ID is incorrect.';
          } else if (apiError.response?.status === 403) {
            errorMessage = 'You do not have permission to view this quote.';
          } else if (apiError.response?.status >= 500) {
            errorMessage = 'Server error. Please try again later.';
          }
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (quoteId) {
      fetchQuoteData();
    } else {
      setError('No quote ID provided');
      setLoading(false);
    }
  }, [quoteId]); // Re-run when quoteId changes

  const generateClientSidePdf = async () => {
    if (!pdfRef.current || downloading) return;

    setDownloading(true);

    try {
      console.log('Generating PDF using html2pdf.js');

      // Get quote number for filename
      const quoteNumber = quote?.quote_no || 'quote';

      const options = {
        margin: 10,
        filename: `${quoteNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait'
        }
      };

      await html2pdf().set(options).from(pdfRef.current).save();
      console.log('PDF generated and downloaded successfully');

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownload = async () => {
    if (!quoteId || downloading) return;

    setDownloading(true);

    try {
      if (printMode) {
        // In print mode, just open the print dialog directly
        console.log('Print mode activated, opening print dialog');
        window.print();
        return;
      }

      console.log('Trying backend PDF generation first');

      try {
        // Try to generate the PDF blob from the backend first
        const pdfBlob = await generateQuotePdf(quoteId);
        console.log('Backend PDF generated successfully, size:', pdfBlob.size, 'bytes');

        // Create a download URL
        const downloadUrl = window.URL.createObjectURL(pdfBlob);

        // Get quote number for filename
        const quoteNumber = quote?.quote_no || 'quote';

        // Create a temporary download link
        const downloadLink = document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.download = `${quoteNumber}.pdf`;
        downloadLink.style.display = 'none';

        // Append to body, click, and remove
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        // Clean up the URL
        window.URL.revokeObjectURL(downloadUrl);

        console.log('Backend PDF download completed successfully');
        return;

      } catch (backendError) {
        console.warn('Backend PDF generation failed, falling back to client-side generation:', backendError);

        // Fallback to client-side PDF generation
        await generateClientSidePdf();
      }

    } catch (error) {
      console.error('Error in download process:', error);
      alert('Failed to download quote PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateStr: string): string => {
    try {
      if (!dateStr) return 'N/A';
      const date = parseISO(dateStr);
      return format(date, 'dd/MM/yyyy');
    } catch (err) {
      return dateStr || 'N/A';
    }
  };

  if (loading) {
    return (
      <ModalOverlay>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>Loading Quote</ModalTitle>
          </ModalHeader>
          <ModalBody style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading quote data</p>
          </ModalBody>
        </ModalContainer>
      </ModalOverlay>
    );
  }

  if (error || !quote) {
    return (
      <ModalOverlay>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>Error</ModalTitle>
          </ModalHeader>
          <ModalBody style={{ textAlign: 'center', padding: '2rem' }}>
            <p>{error || 'Failed to load quote data'}</p>
            <Button onClick={onClose}>Close</Button>
          </ModalBody>
        </ModalContainer>
      </ModalOverlay>
    );
  }

  // Extract data from API response with better error handling
  const quoteNumber = quote.quote_no || 'N/A';
  const customerName = companyName || quote.customer_name || 'N/A';
  const date = formatDate(quote.date);

  // Handle items array - it could be nested in an 'items' property or be the direct array
  let itemsArray: any[] = [];
  if (quote.quote_items) {
    if (Array.isArray(quote.quote_items)) {
      itemsArray = quote.quote_items;
    }
  }

  console.log('Processing items array:', itemsArray);

  const items: QuoteItem[] = itemsArray.map((item: any, index: number) => {
    console.log(`Processing item ${index}:`, item);

    // Improve how we extract values from items, providing better fallbacks for draft quotes
    return {
      ...item,
      code: item.sku || item.sku_code || item.code || '',
      style_code: item.style_code || null,
      description: item.description || '',
      quantity: {
        units: item.units ?? item.quantity_units ?? null,
        boxes: item.boxes ?? item.quantity_boxes ?? null,
        pieces: item.pieces ?? item.quantity_pieces ?? null,
        m2: item.m2 ?? item.quantity_m2 ?? null,
        blm: item.blm ?? item.quantity_blm ?? null,
        total: (Number(item.units) || Number(item.quantity_units) || 0) +
          (Number(item.boxes) || Number(item.quantity_boxes) || 0) +
          (Number(item.pieces) || Number(item.quantity_pieces) || 0) +
          (Number(item.m2) || Number(item.quantity_m2) || 0)
          // BLM is excluded from total calculation as per requirements
      },
      unitPrice: Number(item.unit_price) || Number(item.unitPrice) || 0,
      totalPrice: Number(item.total_price) || Number(item.totalPrice) || 0
    };
  });

  // Calculate financial totals with better error handling
  const orderTotal = typeof quote.total_order === 'string'
    ? parseFloat(quote.total_order) || 0
    : (quote.total_order || 0);

  const gst = typeof quote.total_gst === 'string'
    ? parseFloat(quote.total_gst) || 0
    : (quote.total_gst || 0);
    
  const shipping = typeof quote.shipping === 'string'
    ? parseFloat(quote.shipping) || 0
    : (quote.shipping || 0);

  const grandTotal = typeof quote.grand_total === 'string'
    ? parseFloat(quote.grand_total) || 0
    : (quote.grand_total || 0);

  console.log('Rendered quote data:', {
    quoteNumber,
    customerName,
    date,
    itemsCount: items.length,
    orderTotal,
    gst,
    shipping,
    grandTotal
  });

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Quote View</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <PrintableContent ref={pdfRef}>
            <QuoteContainer>
              <QuoteTitle>QUOTE</QuoteTitle>

              <QuoteHeader>
                <CompanyLogo>
                  <img src={builderLogo} alt="Builders Warehouse" />
                </CompanyLogo>

                <CompanyInfo>
                  <p className="company-name">BUILDERS WAREHOUSE PTY LTD (ACN 654 926 691)</p>
                  <p>Trading as Builders Warehouse Australia</p>
                  <p>(ABN 30 645 926 691)</p>
                  <p></p>
                  <p>Office & Showroom: {quote?.store_type?.toLowerCase() === 'sale' ? '257-259 York St, Sale VIC 3850, Australia' : '214 High St, Cranbourne VIC 3977, Australia'}</p>
                  <p>www.builderswarehouseaustralia.com.au</p>
                  <p>Tel: {quote?.store_type?.toLowerCase() === 'sale' ? '+61 341189555 | sale@builderswarehouseaustralia.com.au' : '+61 387649142 | cranbourne@builderswarehouseaustralia.com.au'}</p>
                </CompanyInfo>
              </QuoteHeader>

              <MainContentContainer>
                <LeftSection>
                  <InfoTable>
                    <InfoCell>
                      <InfoLabel>QUOTE TO:</InfoLabel>
                      {customerName}
                    </InfoCell>
                  </InfoTable>
                </LeftSection>

                <RightSection>
                  <QuoteLabel>QUOTE</QuoteLabel>
                  <QuoteDetails>
                    <QuoteDetail>
                      <span>Quote Number:</span>
                      <span>{quoteNumber}</span>
                    </QuoteDetail>
                    <QuoteDetail>
                      <span>Date:</span>
                      <span>{date}</span>
                    </QuoteDetail>
                    <QuoteDetail>
                      <span>Quote Reference:</span>
                      <span>{quote.reference || 'N/A'}</span>
                    </QuoteDetail>
                  </QuoteDetails>
                </RightSection>
              </MainContentContainer>

              <QuoteTable>
                <thead>
                  <tr>
                    <th className="code-column">Style Code</th>
                    <th className="description-column">Description</th>
                    <th className="quantity-column">Units</th>
                    <th className="quantity-column">Boxes</th>
                    <th className="quantity-column">Pieces</th>
                    <th className="quantity-column">M²</th>
                    <th className="quantity-column">BLM</th>
                    <th className="unit-price-column">Unit Price<br />exc. GST</th>
                    <th className="total-price-column" style={{ width: '14%' }}>Total Price<br />(excluding GST)</th>
                    <th className="gst-column" style={{ width: '6%' }}>GST</th>
                    <th className="total-price-column" style={{ width: '14%' }}>Total Price<br />(including GST)</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? (
                    items.map((item, index) => (
                      <tr key={index}>
                        <td className="code-column">{item.style_code || 'N/A'}</td>
                        <td className="description-column">{item.description}</td>
                        <td className="quantity-column">{item.quantity?.units || ''}</td>
                        <td className="quantity-column">{item.quantity?.boxes || ''}</td>
                        <td className="quantity-column">{item.quantity?.pieces || ''}</td>
                        <td className="quantity-column">{item.quantity?.m2 || ''}</td>
                        <td className="quantity-column">{item.quantity?.blm || ''}</td>
                        <td className="unit-price-column">${(item.unitPrice || 0).toFixed(2)}</td>
                        <td className="total-price-column">${(item.totalPrice || ((item.quantity?.total || 0) * (item.unitPrice || 0))).toFixed(2)}</td>
                        <td className="gst-column">${((item.totalPrice || ((item.quantity?.total || 0) * (item.unitPrice || 0))) * 0.1).toFixed(2)}</td>
                        <td className="total-price-column">${((item.totalPrice || ((item.quantity?.total || 0) * (item.unitPrice || 0))) * 1.1).toFixed(2)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={11} style={{ textAlign: 'center', padding: '20px' }}>
                        No items found
                      </td>
                    </tr>
                  )}
                </tbody>
              </QuoteTable>

              <OrderTotal>
                <div>Order Total:</div>
                <div>${orderTotal.toFixed(2)}</div>
                <div>Shipping:</div>
                <div>${shipping.toFixed(2)}</div>
                <div>GST (10%):</div>
                <div>${gst.toFixed(2)}</div>
                <div>Grand Total:</div>
                <div>${grandTotal.toFixed(2)}</div>
              </OrderTotal>

              <QuoteInfo>
                <div style={{ display: 'flex', gap: '20px', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1 }}>
                    <p><strong>Terms & Conditions:</strong></p>
                    <p>• This quote is valid for 30 days from the date shown above.</p>
                    <p>• Payment terms: 30 days net.</p>
                    <p>• Prices include GST unless otherwise stated.</p>
                    <p>• Delivery charges may apply.</p>
                    <p>• All materials are subject to availability.</p>
                    <p><strong>Contact us to confirm your order:</strong> {quote?.store_type?.toLowerCase() === 'sale' ? 'sale@builderswarehouse.com.au' : 'cranbourne@builderswarehouse.com.au'}</p>
                    {quote.notes && (
                      <>
                        <p><strong>Additional Notes:</strong></p>
                        <p>{quote.notes}</p>
                      </>
                    )}
                  </div>
                  <div style={{ flex: 1, borderLeft: '2px solid #333', paddingLeft: '15px' }}>
                    <p><strong>Payment Details:</strong></p>
                    {(() => {
                      const storeType = typeof quote?.store_type === 'string' 
                        ? quote.store_type.toLowerCase() 
                        : quote?.store_type?.name?.toLowerCase();
                      
                      if (storeType === 'sale') {
                        return (
                          <>
                            <p>BSB: 013542</p>
                            <p>ACC: 434148002</p>
                            <p>Account Name: Builders Warehouse Australia</p>
                          </>
                        );
                      } else {
                        // Default to Cranbourne
                        return (
                          <>
                            <p>BSB: 013542</p>
                            <p>Account: 430892002</p>
                            <p>Account Name: Builders Warehouse Australia</p>
                          </>
                        );
                      }
                    })()}
                  </div>
                </div>
              </QuoteInfo>
            </QuoteContainer>
          </PrintableContent>

          <ButtonContainer>
            <Button
              primary
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? 'Generating PDF...' : 'Download PDF'}
            </Button>
            <Button onClick={onClose}>
              Close
            </Button>
          </ButtonContainer>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default QuoteView;
